'use client';

// Storefront IconButton — square icon-only button.
// Same variant/color/size/shape/state matrix as Button.

import { SpinnerGap } from '@phosphor-icons/react';
import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import type { ButtonVariant, ButtonColor, ButtonSize, ButtonShape } from './button';

type IconButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  color?: ButtonColor;
  size?: ButtonSize;
  shape?: ButtonShape;
  icon: ReactNode;
  /**
   * Work is in flight: swaps the icon for a spinner, marks the button
   * `aria-busy`, and disables it so the action cannot be fired twice.
   */
  loading?: boolean;
};

// ── Square sizes (equal width/height) ──

const SIZE: Record<ButtonSize, string> = {
  xs: 'h-7 w-7',
  sm: 'h-8 w-8',
  md: 'h-9 w-9',
  lg: 'h-11 w-11',
};

const ICON_SIZE: Record<ButtonSize, string> = {
  xs: '[&_svg]:h-3.5 [&_svg]:w-3.5',
  sm: '[&_svg]:h-4 [&_svg]:w-4',
  md: '[&_svg]:h-[18px] [&_svg]:w-[18px]',
  lg: '[&_svg]:h-5 [&_svg]:w-5',
};

const SHAPE: Record<ButtonShape, string> = {
  default: 'rounded-[var(--radius-field)]',
  rounded: 'rounded-full',
};

// xs (28px) and sm (32px) are drawn under Apple's 44pt floor. A transparent
// pseudo-element widens the hit area to a 44×44 square without changing the
// drawn size. md (36px) and lg (44px) are handled below.
const TAP_TARGET =
  "relative before:absolute before:left-1/2 before:top-1/2 before:h-11 before:w-11 before:-translate-x-1/2 before:-translate-y-1/2 before:content-['']";

const TAP_TARGET_BY_SIZE: Record<ButtonSize, string> = {
  xs: TAP_TARGET,
  sm: TAP_TARGET,
  // 36px is still under the floor — icon-only controls are the hardest to hit,
  // so md gets the expansion too. lg is already 44px.
  md: TAP_TARGET,
  lg: '',
};

// Re-use the color/variant logic from Button
// Inline to avoid circular deps — same mapping

type ColorTokens = {
  bg: string;
  bgHover: string;
  text: string;
  border: string;
  soft: string;
  softText: string;
  softHover: string;
  gradient: string;
};

const COLORS: Record<ButtonColor, ColorTokens> = {
  default: { bg: 'bg-foreground', bgHover: 'hover:bg-foreground/90', text: 'text-background', border: 'border-foreground/20', soft: 'bg-foreground/10', softText: 'text-foreground', softHover: 'hover:bg-foreground/15', gradient: 'bg-gradient-to-b from-foreground/80 to-foreground' },
  primary: { bg: 'bg-primary', bgHover: 'hover:bg-primary/90', text: 'text-primary-foreground', border: 'border-primary/30', soft: 'bg-primary/10', softText: 'text-primary', softHover: 'hover:bg-primary/20', gradient: 'bg-gradient-to-b from-primary/80 to-primary' },
  secondary: { bg: 'bg-secondary', bgHover: 'hover:bg-secondary/80', text: 'text-secondary-foreground', border: 'border-secondary-foreground/20', soft: 'bg-secondary', softText: 'text-secondary-foreground', softHover: 'hover:bg-secondary/80', gradient: 'bg-gradient-to-b from-muted/60 to-muted/80' },
  accent: { bg: 'bg-accent', bgHover: 'hover:bg-accent/90', text: 'text-accent-foreground', border: 'border-accent/30', soft: 'bg-accent/10', softText: 'text-accent', softHover: 'hover:bg-accent/20', gradient: 'bg-gradient-to-b from-accent/80 to-accent' },
  info: { bg: 'bg-info', bgHover: 'hover:bg-info/90', text: 'text-info-foreground', border: 'border-info/30', soft: 'bg-info/10', softText: 'text-info', softHover: 'hover:bg-info/20', gradient: 'bg-gradient-to-b from-info/80 to-info' },
  success: { bg: 'bg-success', bgHover: 'hover:bg-success/90', text: 'text-success-foreground', border: 'border-success/30', soft: 'bg-success/10', softText: 'text-success', softHover: 'hover:bg-success/20', gradient: 'bg-gradient-to-b from-success/80 to-success' },
  warning: { bg: 'bg-warning', bgHover: 'hover:bg-warning/90', text: 'text-warning-foreground', border: 'border-warning/30', soft: 'bg-warning/10', softText: 'text-warning', softHover: 'hover:bg-warning/20', gradient: 'bg-gradient-to-b from-warning/80 to-warning' },
  error: { bg: 'bg-danger', bgHover: 'hover:bg-danger/90', text: 'text-danger-foreground', border: 'border-danger/30', soft: 'bg-danger/10', softText: 'text-danger', softHover: 'hover:bg-danger/20', gradient: 'bg-gradient-to-b from-danger/80 to-danger' },
};

function variantClasses(variant: ButtonVariant, color: ButtonColor): string {
  const c = COLORS[color];
  switch (variant) {
    case 'gradient': return `${c.gradient} ${c.text} ${c.bgHover} border-none shadow-sm`;
    case 'solid': return `${c.bg} ${c.text} ${c.bgHover} border-none shadow-sm`;
    case 'soft': return `${c.soft} ${c.softText} ${c.softHover} border-none`;
    case 'outline': return `bg-transparent ${c.softText} border ${c.border} ${c.softHover}`;
    case 'text': return `bg-transparent ${c.softText} border-none ${c.softHover}`;
    case 'noise': return `${c.bg} ${c.text} ${c.bgHover} border-none shadow-sm`;
  }
}

const BASE = 'inline-flex items-center justify-center shrink-0 select-none cursor-pointer transition-all duration-150 active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none';

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  {
    variant = 'solid',
    color = 'primary',
    size = 'md',
    // Icon-only buttons default to a full circle across the storefront for
    // visual consistency. Pass `shape="default"` to opt into the square/rounded-rect
    // form (matching the text Button's radius) when the surrounding layout demands it.
    shape = 'rounded',
    icon,
    loading = false,
    className,
    type,
    disabled,
    ...rest
  },
  ref,
) {
  const cls = [
    BASE,
    SIZE[size],
    ICON_SIZE[size],
    TAP_TARGET_BY_SIZE[size],
    SHAPE[shape],
    variantClasses(variant, color),
    className ?? '',
  ].filter(Boolean).join(' ');

  return (
    <button
      ref={ref}
      // React leaves <button> at the HTML default of type="submit"; an icon
      // button inside a <form> would submit it by accident.
      type={type ?? 'button'}
      className={cls}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? <SpinnerGap className="animate-spin" aria-hidden="true" /> : icon}
    </button>
  );
});
